
import React from 'react';
import { MessageSquare, FileText, Gauge } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Paper } from '@/types/paper';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface PaperCardFooterProps {
  paper: Paper;
  featured?: boolean;
}

const PaperCardFooter: React.FC<PaperCardFooterProps> = ({ paper, featured = false }) => {
  // Memoize date formatting to avoid recalculation on every render
  const timeAgo = React.useMemo(
    () => formatDistanceToNow(new Date(paper.updatedAt), { addSuffix: true }),
    [paper.updatedAt]
  );
  
  // Memoize rating color and display
  const ratingDisplay = React.useMemo(() => {
    if (!paper.averageRating || !paper.reviewCount || paper.reviewCount === 0) {
      return null;
    }
    
    const rating = paper.averageRating;
    let colorClass = 'text-red-600 dark:text-red-400';
    if (rating >= 7.5) {
      colorClass = 'text-green-600 dark:text-green-400';
    } else if (rating >= 5) {
      colorClass = 'text-amber-600 dark:text-amber-400';
    }
    
    return { rating, colorClass };
  }, [paper.averageRating, paper.reviewCount]);
  
  if (featured) {
    // Featured paper: single row layout with all stats and timestamp
    return (
      <div className="flex flex-col gap-2 md:gap-3 pt-3 md:pt-4 mt-auto border-t border-border/30">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="flex flex-wrap items-center gap-3 md:gap-6">
            {typeof paper.reviewCount === 'number' && paper.reviewCount > 0 && (
              <span className="flex items-center gap-2">
                <span className="flex items-center text-xs text-muted-foreground">
                  <FileText className="h-3 w-3 md:h-3.5 md:w-3.5 mr-1 md:mr-1.5 flex-shrink-0" />
                  <span className="truncate">{paper.reviewCount} review{paper.reviewCount !== 1 ? 's' : ''}</span>
                </span>
                {ratingDisplay && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
              <span className={`flex items-center text-xs font-medium whitespace-nowrap py-0.5 ${ratingDisplay.colorClass}`}>
                <Gauge className="h-3 w-3 md:h-3.5 md:w-3.5 mr-1 flex-shrink-0" />
                <span>{ratingDisplay.rating.toFixed(1)}/10</span>
              </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Average rating from {paper.reviewCount} review{paper.reviewCount !== 1 ? 's' : ''}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </span>
            )}
            
            {typeof paper.commentCount === 'number' && paper.commentCount > 0 && (
              <span className="flex items-center text-xs text-muted-foreground">
                <MessageSquare className="h-3 w-3 md:h-3.5 md:w-3.5 mr-1 md:mr-1.5 flex-shrink-0" />
                <span className="truncate">{paper.commentCount} comment{paper.commentCount !== 1 ? 's' : ''}</span>
              </span>
            )}
          </div>
          
          <div className="text-xs text-muted-foreground whitespace-nowrap">
            {timeAgo}
          </div>
        </div>
      </div>
    );
  }
  
  // Regular paper cards: mobile-optimized layout
  return (
    <div className="flex flex-col gap-2 md:gap-3 pt-3 md:pt-4 pb-1 mt-auto border-t border-border/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {typeof paper.reviewCount === 'number' && paper.reviewCount > 0 && (
            <>
              <span className="flex items-center text-xs text-muted-foreground">
                <FileText className="h-3 w-3 md:h-3.5 md:w-3.5 mr-1 md:mr-1.5 flex-shrink-0" />
                <span className="truncate">{paper.reviewCount} review{paper.reviewCount !== 1 ? 's' : ''}</span>
              </span>
              {ratingDisplay && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                  <span className={`flex items-center text-xs font-medium whitespace-nowrap py-0.5 ${ratingDisplay.colorClass}`}>
                    <Gauge className="h-3 w-3 md:h-3.5 md:w-3.5 mr-1 flex-shrink-0" />
                    <span>{ratingDisplay.rating.toFixed(1)}/10</span>
                  </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Average rating from {paper.reviewCount} review{paper.reviewCount !== 1 ? 's' : ''}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </>
          )}
          
          {typeof paper.commentCount === 'number' && paper.commentCount > 0 && (
            <span className="flex items-center text-xs text-muted-foreground">
              <MessageSquare className="h-3 w-3 md:h-3.5 md:w-3.5 mr-1 md:mr-1.5 flex-shrink-0" />
              <span className="truncate">{paper.commentCount} comment{paper.commentCount !== 1 ? 's' : ''}</span>
            </span>
          )}
        </div>
        
        <div className="text-xs text-muted-foreground whitespace-nowrap">
          {timeAgo}
        </div>
      </div>
    </div>
  );
};

export default PaperCardFooter;
